import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiX, FiSave, FiDollarSign, FiSearch, FiLink, FiExternalLink, FiMapPin, FiUpload, FiDownload, FiPrinter, FiMoreVertical, FiBookOpen, FiCheckCircle } from 'react-icons/fi';
import { downloadCustomerTemplate, importCustomersFromRows, readExcelFile } from '../../utils/partyImportHelper';
import { printCustomers } from '../../utils/masterPrintHelper';
import { colors } from '../../theme/colors';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, where, writeBatch } from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, upsertLocalMaster, getEmployeesCached, getCategoriesCached, getSubCategoriesCached } from '../../utils/masterDataStore';
import { SUBCAT_DISCOUNT_MODULE_ID, cleanSubCategoryDiscounts } from '../../utils/customerSubCategoryDiscount';
import { isValidEmail, isValidPhone, writeAuditLog } from '../../utils/validationHelpers';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { createLinkedAccount, ensureParentAccountExists, getLinkedAccount, deleteLinkedAccount } from '../../utils/coaInitializer';
import { pricingDocId } from '../../utils/customerPricingHelper';
// Balance column removed 2026-07-16 (Firebase cost): loadCustomerBalances scanned
// the full-FY subTransactions on every page open. Balances live in the
// Customer Balances / AR Aging reports and the per-customer ledger link.
import ItemSelect from '../../components/ItemSelect';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTaxTerms from '../../hooks/useTaxTerms';
import { useNavigate } from 'react-router-dom';
import {
  MasterHeaderActions,
  MasterAddButton,
  ContentCard,
  StatsRow,
  StatCard,
  StatIcon,
  StatInfo,
  StatValue,
  StatLabel,
  MasterSearchBar,
  MasterSearchInput,
  SearchIcon,
  MasterInput,
  MasterTableContainer,
  MasterTable,
  MasterThead,
  MasterTh,
  MasterTbody,
  MasterTr,
  MasterTd,
  MasterBadge,
  MasterActionButtons,
  MasterIconButton,
  MasterModal,
  MasterModalContent,
  MasterModalHeader,
  MasterModalTitle,
  MasterCloseButton,
  MasterForm,
  MasterFormGroup,
  MasterLabel,
  MasterFormInput,
  MasterSelect,
  MasterTextArea,
  MasterFormRow,
  MasterModalFooter,
  MasterButton,
  MasterEmptyState,
  MasterEmptyIcon,
  MasterEmptyText
} from '../../components/SharedStyles';
import useEscapeClose from '../../hooks/useEscapeClose';

const AccountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}30;
  }
`;

const ActionDropdown = styled.div`
  position: fixed;
  min-width: 200px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 9999;
  padding: 4px;
  overflow: hidden;
`;
const ActionItem = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none; border: none; text-align: left;
  font-size: 13px; cursor: pointer;
  color: ${p => p.$danger ? colors.error.main : colors.text.primary};
  border-radius: 6px;
  &:hover { background: ${p => p.$danger ? `${colors.error.main}10` : colors.neutral[50]}; }
  svg { flex-shrink: 0; opacity: 0.75; }
`;
const ActionDivider = styled.div`
  height: 1px; background: ${colors.neutral[200]}; margin: 4px 0;
`;

const ImportBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: white; color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}50;
  border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer;
  &:hover { background: ${colors.primary.main}10; }
`;
const ImportOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1100;
`;
const ImportModalBox = styled.div`
  background: white; border-radius: 10px; width: 92%; max-width: 520px;
  max-height: 80vh; overflow-y: auto;
`;
const ImportHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid ${colors.neutral[200]};
  h3 { margin: 0; font-size: 15px; }
`;
const ImportBody = styled.div`padding: 18px;`;
const ImportFooter = styled.div`
  display: flex; justify-content: flex-end;
  padding: 12px 18px; border-top: 1px solid ${colors.neutral[200]};
`;
const ProgressBarOuter = styled.div`
  width: 100%; height: 8px; background: ${colors.neutral[200]}; border-radius: 4px; overflow: hidden;
`;
const ProgressBarFill = styled.div`
  height: 100%; background: ${colors.primary.main}; transition: width 0.2s;
`;
const ResultRow = styled.div`
  padding: 8px 12px; margin-bottom: 6px; border-radius: 6px;
  background: ${p => p.$color}15; color: ${p => p.$color};
  font-size: 13px;
`;
const ErrorList = styled.div`
  margin-top: 12px; padding: 10px 12px;
  background: ${colors.neutral[50]}; border-radius: 6px;
  max-height: 220px; overflow-y: auto;
`;
const ErrorItem = styled.div`
  font-size: 12px; color: ${colors.text.secondary};
  padding: 4px 0; border-bottom: 1px dashed ${colors.neutral[200]};
  &:last-child { border-bottom: none; }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AccountCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 11px;
  color: ${colors.primary.main};
  font-weight: 600;
`;

const CreateAccountButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px dashed ${colors.primary.main};
  border-radius: ${colors.borderRadius.sm};
  background: transparent;
  color: ${colors.primary.main};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}10;
    border-style: solid;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Customer-wise Items & Pricing tab styles ───────────────────────
const PricingTabStrip = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  margin-bottom: 16px;
`;

const PricingTabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.primary.main : 'transparent')};
  color: ${({ $active }) => ($active ? colors.primary.main : colors.text.secondary)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  margin-bottom: -1px;
  &:hover { color: ${colors.primary.main}; }
`;

const PricingCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
`;

const PricingHelp = styled.div`
  padding: 10px 12px;
  background: ${colors.primary.main}08;
  border-left: 3px solid ${colors.primary.main};
  border-radius: 4px;
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.5;
  margin-bottom: 14px;
`;

const PricingExclusiveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  margin-bottom: 8px;
`;

const PricingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
`;

const PricingTh = styled.th`
  padding: 8px 10px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  background: ${colors.neutral?.[50] || '#f9fafb'};
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
`;

const PricingTd = styled.td`
  padding: 8px 10px;
  font-size: 13px;
  border-bottom: 1px solid ${colors.neutral?.[100] || '#f3f4f6'};
  vertical-align: middle;
`;

// Sub-Category Discount tab (module: customer_subcategory_discount).
const ScdPctInput = styled.input`
  width: 90px; padding: 6px 8px; text-align: right; font-size: 13px; font-family: inherit;
  border: 1px solid ${({ $warn }) => ($warn ? '#DD6B20' : (colors.neutral?.[300] || '#d1d5db'))};
  background: ${({ $warn }) => ($warn ? '#FFFAF0' : 'white')};
  border-radius: 4px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const ScdWarn = styled.span`
  margin-left: 8px; font-size: 11px; color: #C05621;
`;
const ScdCatRow = styled.tr`
  td { background: ${colors.neutral?.[50] || '#f9fafb'}; font-weight: 600; }
`;

const Customers = () => {
  const { userProfile, activeCompany, currentUser, isModuleEnabled, activeBranch, activeFinancialYear } = useAuth();
  const { taxLabel, registrationLabel, registrationLabelFull } = useTaxTerms();
  const customerPricingEnabled = typeof isModuleEnabled === 'function'
    ? isModuleEnabled('sales_customer_pricing')
    : false;
  const diEnabled = typeof isModuleEnabled === 'function'
    ? isModuleEnabled('fbr_digital_invoicing')
    : false;
  const DI_PROVINCES = ['Punjab', 'Sindh', 'Khyber Pakhtunkhwa', 'Balochistan', 'Islamabad Capital Territory', 'Gilgit-Baltistan', 'Azad Jammu and Kashmir'];
  const customerClassEnabled = typeof isModuleEnabled === 'function'
    ? isModuleEnabled('customer_class')
    : false;
  // Customer Discount by Sub-Category (module, off by default).
  const subCatDiscEnabled = typeof isModuleEnabled === 'function'
    ? isModuleEnabled(SUBCAT_DISCOUNT_MODULE_ID)
    : false;
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'customers');
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [linkedAccounts, setLinkedAccounts] = useState({});
  // Live balances keyed by customerId, computed from subTransactions so the
  // Balance column always matches Reports → Customer Balances.
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(null);

  // Regions and Areas
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);

  // Excel import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Actions menu (portal-based so it escapes table overflow clipping)
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const closeActionMenu = () => setOpenActionMenuId(null);
  const toggleActionMenu = (customerId, e) => {
    if (openActionMenuId === customerId) { closeActionMenu(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 200;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    const top = rect.bottom + 4;
    setActionMenuPos({ top, left: Math.max(8, left) });
    setOpenActionMenuId(customerId);
  };
  useEffect(() => {
    if (!openActionMenuId) return;
    const close = (e) => {
      if (!e.target.closest('[data-action-menu]') && !e.target.closest('[data-action-trigger]')) closeActionMenu();
    };
    const onScroll = () => closeActionMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openActionMenuId]);

  const handleOpenLedger = (customer) => {
    setOpenActionMenuId(null);
    if (!customer.linkedAccountId) {
      alert('No linked account for this customer. Please create one first.');
      return;
    }
    navigate('/reports/ledger', { state: { filterAccountId: customer.linkedAccountId } });
  };

  const handleOpenReceipt = (customer) => {
    setOpenActionMenuId(null);
    navigate('/receipts/cash-receipt', {
      state: {
        prefill: {
          partyId: customer.id,
          partyName: customer.companyName,
          linkedAccountId: customer.linkedAccountId || null
        }
      }
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!canEdit) { alert('You do not have permission to import.'); return; }
    setImporting(true);
    setImportResults(null);
    setShowImportModal(true);
    try {
      const rows = await readExcelFile(file);
      if (rows.length === 0) {
        setImportResults({ success: 0, skipped: 0, failed: 0, errors: ['No rows found in the file.'] });
        setImporting(false);
        return;
      }
      const results = await importCustomersFromRows(
        activeCompany?.id, rows, userProfile?.uid,
        (p) => setImportProgress(p)
      );
      setImportResults(results);
      loadCustomers();
    } catch (err) {
      console.error(err);
      setImportResults({ success: 0, skipped: 0, failed: 0, errors: ['Import failed: ' + err.message] });
    } finally {
      setImporting(false);
    }
  };

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    ntn: '',
    strn: '',
    province: '',
    registrationType: '',
    address: '',
    regionId: '',
    areaId: '',
    postalCode: '',
    country: 'Pakistan',
    creditLimit: 0,
    creditDays: 30,
    isActive: true,
    notes: '',
    pricingExclusive: false,
    // Default salesperson — stored as employeeId. Name kept alongside for cheap
    // display on sale forms / aging reports without an extra read.
    salesPersonId: '',
    salesPersonName: '',
    // Per-customer sale defaults. '' = not set → sale lines use the standard rate.
    defaultDiscountPercent: '',
    defaultTaxRate: '',
    customerClass: ''
  });

  // Employees list — fed into the salesperson dropdown. Loaded once on mount.
  const [employees, setEmployees] = useState([]);

  // ── Customer-wise Items & Pricing (module: sales_customer_pricing) ──
  // Active modal tab: 'profile' | 'pricing' | 'subcat'.
  const [modalTab, setModalTab] = useState('profile');
  // ── Sub-Category Discount tab: { subCategoryId: typed % } + the lists ──
  const [subCatDisc, setSubCatDisc] = useState({});
  const [scdLists, setScdLists] = useState({ cats: [], subs: [] });
  const [scdSearch, setScdSearch] = useState('');
  const loadSubCategoryLists = async () => {
    if (!activeCompany?.id) return;
    try {
      const [cats, subs] = await Promise.all([getCategoriesCached(activeCompany.id), getSubCategoriesCached(activeCompany.id)]);
      setScdLists({
        cats: (cats || []).filter(c => c.isDeleted !== true),
        subs: (subs || []).filter(s => s.isDeleted !== true),
      });
    } catch (e) { console.error('Load sub-categories failed:', e); }
  };
  // Items master cache for the picker. Loaded lazily once when the module is on.
  const [itemsList, setItemsList] = useState([]);
  // Working set of override rows for the customer being edited.
  // Each row: { docId, itemId, itemCode, itemName, price, _isNew }
  // `docId` mirrors the Firestore doc id (`{customerId}__{itemId}`); kept so we
  // can spot deletions on save. `_isNew` rows haven't been written yet.
  const [pricingRows, setPricingRows] = useState([]);
  // Snapshot of the rows that were loaded from Firestore — diffed against
  // pricingRows on submit to compute deletions.
  const [pricingOriginal, setPricingOriginal] = useState([]);

  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);

      // Load customers
      const customersRef = collection(db, 'companies', activeCompany?.id, 'customers');
      const q = query(customersRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);

      const customersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setCustomers(customersList);
      setFilteredCustomers(customersList);

      // Load linked accounts for all customers
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const accountsQuery = query(accountsRef, where('linkedEntityType', '==', 'customer'));
      const accountsSnapshot = await getDocs(accountsQuery);

      const accountsMap = {};
      accountsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.linkedEntityId) {
          accountsMap[data.linkedEntityId] = {
            id: doc.id,
            ...data
          };
        }
      });
      setLinkedAccounts(accountsMap);

      // Load regions
      const regionsRef = collection(db, 'companies', activeCompany.id, 'regions');
      const regionsQuery = query(regionsRef, orderBy('regionName'));
      const regionsSnapshot = await getDocs(regionsQuery);
      setRegions(regionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load areas
      const areasRef = collection(db, 'companies', activeCompany.id, 'areas');
      const areasQuery = query(areasRef, orderBy('areaName'));
      const areasSnapshot = await getDocs(areasQuery);
      setAreas(areasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Load employees once per company for the Sales Person dropdown.
  useEffect(() => {
    if (!activeCompany?.id) { setEmployees([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const rows = await getEmployeesCached(activeCompany.id);
        if (cancelled) return;
        setEmployees(
          rows.filter(e => e.isActive !== false && e.isDeleted !== true)
        );
      } catch (err) {
        console.error('Failed to load employees:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompany?.id]);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = customers.filter(customer => {
      // Get region and area names for search
      const region = regions.find(r => r.id === customer.regionId);
      const area = areas.find(a => a.id === customer.areaId);
      const regionName = region?.regionName?.toLowerCase() || '';
      const areaName = area?.areaName?.toLowerCase() || '';

      return customer.companyName?.toLowerCase().includes(query) ||
        customer.contactPerson?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.phone?.includes(query) ||
        customer.ntn?.toLowerCase().includes(query) ||
        regionName.includes(query) ||
        areaName.includes(query);
    });
    setFilteredCustomers(filtered);
  }, [searchQuery, customers, regions, areas]);

  // Lazy-load items master once when the module is on. Used by the pricing tab
  // ItemSelect; never fired when the module is off.
  const loadItemsForPricing = useCallback(async () => {
    if (!customerPricingEnabled || !activeCompany?.id) return;
    if (itemsList.length > 0) return;
    try {
      const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'inventoryItems'));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(i => i.isDeleted !== true)
        .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
      setItemsList(list);
    } catch (err) {
      console.error('Failed to load items for customer pricing:', err);
    }
  }, [customerPricingEnabled, activeCompany?.id, itemsList.length]);

  // Load existing pricing rows for a customer being edited.
  const loadPricingForCustomer = useCallback(async (customerId) => {
    if (!customerPricingEnabled || !customerId || !activeCompany?.id) {
      setPricingRows([]);
      setPricingOriginal([]);
      return;
    }
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'customerItemPricing'),
        where('customerId', '==', customerId)
      ));
      const rows = snap.docs.map(d => {
        const data = d.data();
        return {
          docId: d.id,
          itemId: data.itemId || '',
          itemCode: data.itemCode || '',
          itemName: data.itemName || '',
          price: Number(data.price) || 0,
          _isNew: false
        };
      });
      setPricingRows(rows);
      setPricingOriginal(rows.map(r => ({ ...r })));
    } catch (err) {
      console.error('Failed to load customer pricing:', err);
      setPricingRows([]);
      setPricingOriginal([]);
    }
  }, [customerPricingEnabled, activeCompany?.id]);

  const openAddModal = () => {
    setFormData({
      companyName: '',
      contactPerson: '',
      email: '',
      phone: '',
      ntn: '',
      strn: '',
      province: '',
      registrationType: '',
      address: '',
      regionId: '',
      areaId: '',
      postalCode: '',
      country: 'Pakistan',
      creditLimit: 0,
      creditDays: 30,
      isActive: true,
      notes: '',
      pricingExclusive: false,
      salesPersonId: '',
      salesPersonName: '',
      defaultDiscountPercent: '',
      defaultTaxRate: '',
      customerClass: ''
    });
    setEditingCustomer(null);
    setModalTab('profile');
    setPricingRows([]);
    setPricingOriginal([]);
    if (customerPricingEnabled) loadItemsForPricing();
    setSubCatDisc({});
    setScdSearch('');
    if (subCatDiscEnabled) loadSubCategoryLists();
    setShowModal(true);
  };

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to submit form
  useDocumentShortcuts({
    onNew: openAddModal,
    onSave: () => { const form = document.querySelector('form'); if (form) form.requestSubmit(); },
    viewMode: showModal ? 'form' : 'list',
    saving
  });

  const openEditModal = (customer) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setFormData({
      companyName: customer.companyName || '',
      contactPerson: customer.contactPerson || '',
      email: customer.email || '',
      phone: customer.phone || '',
      ntn: customer.ntn || '',
      strn: customer.strn || '',
      province: customer.province || '',
      registrationType: customer.registrationType || '',
      address: customer.address || '',
      regionId: customer.regionId || '',
      areaId: customer.areaId || '',
      postalCode: customer.postalCode || '',
      country: customer.country || 'Pakistan',
      creditLimit: customer.creditLimit || 0,
      creditDays: customer.creditDays || 30,
      isActive: customer.isActive !== false,
      notes: customer.notes || '',
      pricingExclusive: customer.pricingExclusive === true,
      salesPersonId: customer.salesPersonId || '',
      salesPersonName: customer.salesPersonName || '',
      // `?? ''` keeps an explicit 0 (tax-exempt / no discount) but shows blank when unset.
      defaultDiscountPercent: customer.defaultDiscountPercent ?? '',
      defaultTaxRate: customer.defaultTaxRate ?? '',
      customerClass: customer.customerClass || ''
    });
    setEditingCustomer(customer);
    setModalTab('profile');
    if (customerPricingEnabled) {
      loadItemsForPricing();
      loadPricingForCustomer(customer.id);
    } else {
      setPricingRows([]);
      setPricingOriginal([]);
    }
    const savedScd = customer.subCategoryDiscounts && typeof customer.subCategoryDiscounts === 'object' ? customer.subCategoryDiscounts : {};
    const scdBoxes = {};
    Object.entries(savedScd).forEach(([id, v]) => { if (v !== null && v !== undefined && v !== '') scdBoxes[id] = String(v); });
    setSubCatDisc(scdBoxes);
    setScdSearch('');
    if (subCatDiscEnabled) loadSubCategoryLists();
    setShowModal(true);
  };

  // Persist the pricing rows for `customerId`, diffing against `pricingOriginal`
  // to upsert kept rows and delete removed ones in a single batch. Only the
  // rows the user actually changed are written, so editing a customer's
  // profile without touching the pricing tab is a no-op here.
  const savePricingRows = async (customerId) => {
    if (!customerPricingEnabled || !customerId) return;
    const batch = writeBatch(db);
    const colRef = collection(db, 'companies', activeCompany.id, 'customerItemPricing');

    const seenItemIds = new Set();
    pricingRows.forEach(row => {
      if (!row.itemId) return; // skip incomplete rows silently
      const id = pricingDocId(customerId, row.itemId);
      seenItemIds.add(row.itemId);
      batch.set(doc(colRef, id), {
        customerId,
        itemId: row.itemId,
        itemCode: row.itemCode || '',
        itemName: row.itemName || '',
        price: Number(row.price) || 0,
        updatedAt: new Date().toISOString(),
        ...(row._isNew ? { createdAt: new Date().toISOString(), createdBy: userProfile?.uid || null } : {})
      }, { merge: true });
    });

    // Anything in original but not in seenItemIds → user removed it.
    pricingOriginal.forEach(orig => {
      if (orig.itemId && !seenItemIds.has(orig.itemId)) {
        batch.delete(doc(colRef, orig.docId || pricingDocId(customerId, orig.itemId)));
      }
    });

    await batch.commit();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (formData.email && !isValidEmail(formData.email)) {
      alert('Please enter a valid email address'); return;
    }
    if (formData.phone && !isValidPhone(formData.phone)) {
      alert('Please enter a valid phone number'); return;
    }

    // Validate pricing rows when module is on. Each row needs a unique item +
    // a non-negative price. Empty rows are silently dropped on save.
    if (customerPricingEnabled) {
      const seen = new Set();
      for (const row of pricingRows) {
        if (!row.itemId) continue; // empty row, ignored on save
        if (seen.has(row.itemId)) {
          alert(`Item "${row.itemName || row.itemCode}" appears more than once in the custom pricing list.`);
          return;
        }
        seen.add(row.itemId);
        if (Number(row.price) < 0) {
          alert(`Price for "${row.itemName || row.itemCode}" cannot be negative.`);
          return;
        }
      }
      if (formData.pricingExclusive && seen.size === 0) {
        if (!window.confirm('Restrict mode is ON but no items are listed — this customer will not be able to buy anything until you add items. Save anyway?')) {
          return;
        }
      }
    }

    setSaving(true);

    try {
      const customerData = {
        ...formData,
        // Only persist pricingExclusive when the module is on; otherwise the
        // field stays whatever it was before (default false on new customers).
        pricingExclusive: customerPricingEnabled ? !!formData.pricingExclusive : (formData.pricingExclusive === true),
        // Per-customer sale defaults: blank → null = "not set, use the standard
        // rate"; a number (incl. 0 = tax-exempt / no discount) is an explicit choice.
        defaultDiscountPercent: (formData.defaultDiscountPercent === '' || formData.defaultDiscountPercent == null) ? null : Number(formData.defaultDiscountPercent),
        defaultTaxRate: (formData.defaultTaxRate === '' || formData.defaultTaxRate == null) ? null : Number(formData.defaultTaxRate),
        customerClass: formData.customerClass || null,
        // Sub-category discounts are written only while the module is on, so
        // saving with it off leaves a customer's saved list untouched.
        ...(subCatDiscEnabled ? { subCategoryDiscounts: cleanSubCategoryDiscounts(subCatDisc) } : {}),
        updatedAt: new Date().toISOString()
      };

      // Captured on both branches so the local cache write below knows the id.
      let savedCustomerId = editingCustomer?.id || null;

      if (editingCustomer) {
        // Update existing customer
        const customerRef = doc(db, 'companies', activeCompany?.id, 'customers', editingCustomer.id);
        await updateDoc(customerRef, customerData);

        // If customer has linked account, update its name if changed
        if (editingCustomer.linkedAccountId && editingCustomer.companyName !== formData.companyName) {
          try {
            const linkedAccountRef = doc(db, 'companies', activeCompany?.id, 'accounts', editingCustomer.linkedAccountId);
            await updateDoc(linkedAccountRef, {
              name: `Customer: ${formData.companyName}`,
              updatedAt: new Date().toISOString()
            });
          } catch (accountError) {
            console.error('Error updating linked account:', accountError);
          }
        }
      } else {
        // First validate parent account exists (this will auto-repair COA if needed)
        const parentCheck = await ensureParentAccountExists(activeCompany?.id, 'customer', userProfile.uid);

        if (!parentCheck.success) {
          alert(`Cannot create customer: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
          setSaving(false);
          return;
        }

        // Create new customer
        customerData.createdAt = new Date().toISOString();
        customerData.createdBy = userProfile.uid;

        const customersRef = collection(db, 'companies', activeCompany?.id, 'customers');
        const docRef = await addDoc(customersRef, customerData);
        savedCustomerId = docRef.id;

        // Create linked account using the new simplified API
        try {
          const accountResult = await createLinkedAccount(
            activeCompany?.id,
            'customer',
            docRef.id,
            formData.companyName,
            userProfile.uid
          );

          if (accountResult.success) {
            // Update customer with linked account ID and code
            await updateDoc(doc(customersRef, docRef.id), {
              linkedAccountId: accountResult.accountId,
              linkedAccountCode: accountResult.accountCode
            });
          } else {
            console.warn('Failed to create linked account:', accountResult.message);
            alert('Customer created but failed to create linked account. You can create it manually from the customer list.');
          }
        } catch (accountError) {
          console.error('Error creating linked account:', accountError);
          alert('Customer created but failed to create linked account. You may need to create it manually.');
        }

        // Persist pricing rows for the brand-new customer using the assigned id.
        if (customerPricingEnabled) {
          try { await savePricingRows(docRef.id); } catch (pricingErr) {
            console.error('Error saving customer pricing:', pricingErr);
            alert('Customer created but custom pricing rows failed to save. You can edit the customer to retry.');
          }
        }
      }

      // Save pricing rows for existing-customer edits.
      if (editingCustomer && customerPricingEnabled) {
        try { await savePricingRows(editingCustomer.id); } catch (pricingErr) {
          console.error('Error saving customer pricing:', pricingErr);
          alert('Customer saved but custom pricing rows failed to save. Please retry.');
        }
      }

      // Write the saved customer straight into the device's local cache so it
      // is selectable on an invoice IMMEDIATELY. invalidateMaster() below is
      // fire-and-forget and re-downloads the whole customer list; a user who
      // saves and goes straight to an invoice beats that download, reads the
      // stale cache, and can't find the customer until a hard refresh.
      // (`savedCustomerId` is set on both the create and edit branches above.)
      if (savedCustomerId) {
        await upsertLocalMaster(activeCompany?.id, 'customers', { id: savedCustomerId, ...customerData });
      }

      // Tier 1 (Firebase cost): refresh the device-local master cache the
      // transaction forms read from, so a new/edited customer (and its linked
      // account) appears there without waiting for the change-check window.
      // The linked ACCOUNT still needs the full refresh — it is created by
      // createLinkedAccount() and its generated code/id aren't known here.
      invalidateMaster(activeCompany?.id, 'customers');
      invalidateMaster(activeCompany?.id, 'accounts');

      setShowModal(false);
      loadCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error saving customer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (window.confirm(`Are you sure you want to delete customer "${customer.companyName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'customer', customer.id);
        if (hasReferences) {
          alert(message);
          return;
        }

        const customerRef = doc(db, 'companies', activeCompany?.id, 'customers', customer.id);
        await deleteDoc(customerRef);
        await deleteLinkedAccount(activeCompany.id, 'customer', customer.id);

        // Best-effort cleanup of any custom pricing rows tied to this customer.
        // Runs whether the module is on or off so toggling can't leave orphans.
        try {
          const pricingSnap = await getDocs(query(
            collection(db, 'companies', activeCompany.id, 'customerItemPricing'),
            where('customerId', '==', customer.id)
          ));
          if (!pricingSnap.empty) {
            const batch = writeBatch(db);
            pricingSnap.docs.forEach(d => batch.delete(d.ref));
            await batch.commit();
          }
        } catch (cleanupErr) {
          console.warn('Failed to clean up customer pricing rows on delete:', cleanupErr);
        }

        await writeAuditLog(activeCompany?.id, null, 'DELETE', 'customer', customer.id, `Deleted customer: ${customer.companyName}`);
        invalidateMaster(activeCompany?.id, 'customers');
        invalidateMaster(activeCompany?.id, 'accounts');
        loadCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('Error deleting customer. Please try again.');
      }
    }
  };

  // Create linked account for existing customer
  const handleCreateLinkedAccount = async (customer) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!customer || linkedAccounts[customer.id]) return;

    setCreatingAccount(customer.id);

    try {
      // Ensure parent account exists (will auto-repair COA if needed)
      const parentCheck = await ensureParentAccountExists(activeCompany?.id, 'customer', userProfile.uid);

      if (!parentCheck.success) {
        alert(`Cannot create account: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
        setCreatingAccount(null);
        return;
      }

      const accountResult = await createLinkedAccount(
        activeCompany?.id,
        'customer',
        customer.id,
        customer.companyName,
        userProfile.uid
      );

      if (!accountResult.success) {
        alert(`Failed to create linked account: ${accountResult.message}`);
        setCreatingAccount(null);
        return;
      }

      // Update customer with linked account ID and code
      const customerRef = doc(db, 'companies', activeCompany?.id, 'customers', customer.id);
      await updateDoc(customerRef, {
        linkedAccountId: accountResult.accountId,
        linkedAccountCode: accountResult.accountCode,
        updatedAt: new Date().toISOString()
      });

      loadCustomers();
      alert('Account created successfully!');
    } catch (error) {
      console.error('Error creating linked account:', error);
      alert('Error creating account. Please try again.');
    } finally {
      setCreatingAccount(null);
    }
  };

  // Navigate to ledger with customer account
  const viewLedger = (customerId) => {
    const account = linkedAccounts[customerId];
    if (account) {
      navigate('/reports/ledger', { state: { filterAccountId: account.id } });
    }
  };

  // Get region name by ID
  const getRegionName = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    return region?.regionName || '';
  };

  // Get area name by ID
  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area?.areaName || '';
  };

  // Get areas filtered by selected region
  const getAreasForRegion = (regionId) => {
    if (!regionId) return [];
    return areas.filter(a => a.regionId === regionId);
  };

  // Handle region change - reset area when region changes
  const handleRegionChange = (regionId) => {
    setFormData({ ...formData, regionId, areaId: '' });
  };

  // Get location display string
  const getLocationDisplay = (customer) => {
    const areaName = getAreaName(customer.areaId);
    const regionName = getRegionName(customer.regionId);
    if (areaName && regionName) {
      return `${areaName}, ${regionName}`;
    }
    return areaName || regionName || '-';
  };

  return (
    <PageLayout pageTitle="Customers" breadcrumb="Master Data / Customers">
      <MasterHeaderActions style={{ gap: 10, display: 'flex', flexWrap: 'wrap' }}>
        <ImportBtn
          onClick={() => printCustomers(filteredCustomers, { activeCompany, regions, areas, filterLabel: searchQuery ? `Search: "${searchQuery}"` : '' })}
          title="Print customer list"
        >
          <FiPrinter /> Print
        </ImportBtn>
        <ImportBtn onClick={downloadCustomerTemplate} title="Download Excel template">
          <FiDownload /> Download Template
        </ImportBtn>
        {canEdit && (
          <ImportBtn onClick={() => fileInputRef.current?.click()} title="Import customers from Excel">
            <FiUpload /> Import Excel
          </ImportBtn>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {canEdit && (
          <MasterAddButton onClick={openAddModal}>
            <FiPlus /> Add Customer
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      {(() => {
        const totalCustomers = customers.length;
        const activeCustomers = customers.filter(c => c.isActive !== false).length;
        return (
          <StatsRow>
            <StatCard>
              <StatIcon $color={colors.primary.main}><FiUsers /></StatIcon>
              <StatInfo>
                <StatValue>{totalCustomers}</StatValue>
                <StatLabel>Total Customers</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $color={colors.success.main}><FiCheckCircle /></StatIcon>
              <StatInfo>
                <StatValue>{activeCustomers}</StatValue>
                <StatLabel>Active Customers</StatLabel>
              </StatInfo>
            </StatCard>
          </StatsRow>
        );
      })()}

      {/* Actions menu (portal — escapes table overflow) */}
      {openActionMenuId && (() => {
        const customer = customers.find(c => c.id === openActionMenuId);
        if (!customer) return null;
        return createPortal(
          <ActionDropdown
            data-action-menu
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
          >
            {canChange && (
              <ActionItem onClick={() => { closeActionMenu(); openEditModal(customer); }}>
                <FiEdit2 size={14} /> Edit
              </ActionItem>
            )}
            <ActionItem onClick={() => handleOpenLedger(customer)}>
              <FiBookOpen size={14} /> View Ledger
            </ActionItem>
            <ActionItem onClick={() => handleOpenReceipt(customer)}>
              <FiDollarSign size={14} /> Receive Payment
            </ActionItem>
            {canDelete && (
              <>
                <ActionDivider />
                <ActionItem $danger onClick={() => { closeActionMenu(); handleDelete(customer); }}>
                  <FiTrash2 size={14} /> Delete
                </ActionItem>
              </>
            )}
          </ActionDropdown>,
          document.body
        );
      })()}

      {/* Import Results Modal */}
      {showImportModal && (
        <ImportOverlay onClick={() => !importing && setShowImportModal(false)}>
          <ImportModalBox onClick={(e) => e.stopPropagation()}>
            <ImportHeader>
              <h3>Customer Import</h3>
              {!importing && (
                <button onClick={() => setShowImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <FiX />
                </button>
              )}
            </ImportHeader>
            <ImportBody>
              {importing ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ fontSize: 14, marginBottom: 12 }}>
                    Importing… {importProgress.current} / {importProgress.total}
                  </div>
                  <ProgressBarOuter>
                    <ProgressBarFill style={{ width: `${importProgress.total ? (importProgress.current / importProgress.total) * 100 : 0}%` }} />
                  </ProgressBarOuter>
                </div>
              ) : importResults && (
                <div>
                  <ResultRow $color={colors.success.main}><strong>{importResults.success}</strong> imported</ResultRow>
                  <ResultRow $color={colors.warning.main}><strong>{importResults.skipped}</strong> skipped (already exists)</ResultRow>
                  <ResultRow $color={colors.error.main}><strong>{importResults.failed}</strong> failed</ResultRow>
                  {importResults.errors.length > 0 && (
                    <ErrorList>
                      <strong style={{ fontSize: 12, color: colors.text.secondary }}>Issues:</strong>
                      {importResults.errors.slice(0, 10).map((err, i) => (
                        <ErrorItem key={i}>{err}</ErrorItem>
                      ))}
                      {importResults.errors.length > 10 && (
                        <ErrorItem>...and {importResults.errors.length - 10} more</ErrorItem>
                      )}
                    </ErrorList>
                  )}
                </div>
              )}
            </ImportBody>
            {!importing && (
              <ImportFooter>
                <MasterAddButton onClick={() => setShowImportModal(false)}>Close</MasterAddButton>
              </ImportFooter>
            )}
          </ImportModalBox>
        </ImportOverlay>
      )}

      <ContentCard>
        <MasterSearchBar>
          <MasterSearchInput>
            <SearchIcon><FiSearch /></SearchIcon>
            <MasterInput
              type="text"
              placeholder={`Search by company name, contact person, ${registrationLabel}, phone...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </MasterSearchInput>
        </MasterSearchBar>

        <MasterTableContainer>
          {loading ? (
            <MasterEmptyState>
              <MasterEmptyText>Loading customers...</MasterEmptyText>
            </MasterEmptyState>
          ) : filteredCustomers.length === 0 ? (
            <MasterEmptyState>
              <MasterEmptyIcon><FiUsers /></MasterEmptyIcon>
              <MasterEmptyText>
                {searchQuery ? 'No customers found matching your search' : 'No customers yet'}
              </MasterEmptyText>
            </MasterEmptyState>
          ) : (
            <MasterTable>
              <MasterThead>
                <MasterTr>
                  <MasterTh>Company Name</MasterTh>
                  <MasterTh>Contact Person</MasterTh>
                  <MasterTh>Phone / Email</MasterTh>
                  <MasterTh>Location</MasterTh>
                  <MasterTh>Account</MasterTh>
                  <MasterTh>Status</MasterTh>
                  <MasterTh>Actions</MasterTh>
                </MasterTr>
              </MasterThead>
              <MasterTbody>
                {filteredCustomers.map(customer => {
                  const account = linkedAccounts[customer.id];

                  return (
                    <MasterTr key={customer.id}>
                      <MasterTd data-label="Company Name">
                        <div style={{ fontWeight: 500 }}>{customer.companyName}</div>
                        {customer.ntn && (
                          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
                            {registrationLabel}: {customer.ntn}
                          </div>
                        )}
                      </MasterTd>
                      <MasterTd data-label="Contact Person">{customer.contactPerson || '-'}</MasterTd>
                      <MasterTd data-label="Phone / Email">
                        <div>{customer.phone || '-'}</div>
                        <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                          {customer.email || '-'}
                        </div>
                      </MasterTd>
                      <MasterTd data-label="Location">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FiMapPin size={14} style={{ color: colors.text.secondary }} />
                          {getLocationDisplay(customer)}
                        </div>
                      </MasterTd>
                      <MasterTd data-label="Account">
                        {account ? (
                          <AccountInfo>
                            <AccountBadge onClick={() => viewLedger(customer.id)} title="View Ledger">
                              <FiDollarSign size={12} />
                              <AccountCode>{account.code}</AccountCode>
                              <FiExternalLink size={10} />
                            </AccountBadge>
                          </AccountInfo>
                        ) : (
                          canEdit && (
                            <CreateAccountButton
                              onClick={() => handleCreateLinkedAccount(customer)}
                              disabled={creatingAccount === customer.id}
                              title="Create linked account in Chart of Accounts"
                            >
                              <FiLink size={12} />
                              {creatingAccount === customer.id ? 'Creating...' : 'Create Account'}
                            </CreateAccountButton>
                          )
                        )}
                      </MasterTd>
                      <MasterTd data-label="Status">
                        <MasterBadge active={customer.isActive !== false}>
                          {customer.isActive !== false ? 'Active' : 'Inactive'}
                        </MasterBadge>
                      </MasterTd>
                      <MasterTd data-label="Actions">
                        <MasterIconButton
                          data-action-trigger
                          onClick={(e) => toggleActionMenu(customer.id, e)}
                          title="Actions"
                        >
                          <FiMoreVertical />
                        </MasterIconButton>
                      </MasterTd>
                    </MasterTr>
                  );
                })}
              </MasterTbody>
            </MasterTable>
          )}
        </MasterTableContainer>
      </ContentCard>

      {showModal && (
        <MasterModal>
          <MasterModalContent onClick={(e) => e.stopPropagation()}>
            <MasterModalHeader>
              <MasterModalTitle>
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </MasterModalTitle>
              <MasterCloseButton onClick={() => setShowModal(false)}>
                <FiX size={20} />
              </MasterCloseButton>
            </MasterModalHeader>

            <MasterForm onSubmit={handleSubmit}>
              {(customerPricingEnabled || subCatDiscEnabled) && (
                <PricingTabStrip>
                  <PricingTabBtn
                    type="button"
                    $active={modalTab === 'profile'}
                    onClick={() => setModalTab('profile')}
                  >
                    Profile
                  </PricingTabBtn>
                  {customerPricingEnabled && (
                  <PricingTabBtn
                    type="button"
                    $active={modalTab === 'pricing'}
                    onClick={() => setModalTab('pricing')}
                  >
                    Custom Items & Pricing
                    {pricingRows.filter(r => r.itemId).length > 0 && (
                      <PricingCount>{pricingRows.filter(r => r.itemId).length}</PricingCount>
                    )}
                  </PricingTabBtn>
                  )}
                  {subCatDiscEnabled && (
                    <PricingTabBtn
                      type="button"
                      data-scd-tab
                      $active={modalTab === 'subcat'}
                      onClick={() => setModalTab('subcat')}
                    >
                      Sub-Category Discount
                      {Object.keys(cleanSubCategoryDiscounts(subCatDisc)).length > 0 && (
                        <PricingCount>{Object.keys(cleanSubCategoryDiscounts(subCatDisc)).length}</PricingCount>
                      )}
                    </PricingTabBtn>
                  )}
                </PricingTabStrip>
              )}

              <div style={{ display: modalTab === 'profile' ? 'block' : 'none' }}>
              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Company Name *</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="ABC Traders Pvt Ltd"
                    required
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Contact Person</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    placeholder="Ahmed Khan"
                  />
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Email</MasterLabel>
                  <MasterFormInput
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@company.pk"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Phone *</MasterLabel>
                  <MasterFormInput
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+92 300 1234567"
                    required
                  />
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>{registrationLabel} ({registrationLabelFull})</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.ntn}
                    onChange={(e) => setFormData({ ...formData, ntn: e.target.value })}
                    placeholder="1234567-8"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>{taxLabel === 'VAT' ? 'VAT Number' : 'STRN (Sales Tax Registration)'}</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.strn}
                    onChange={(e) => setFormData({ ...formData, strn: e.target.value })}
                    placeholder={taxLabel === 'VAT' ? 'e.g., 100000000003' : '12-34-5678-901-23'}
                  />
                </MasterFormGroup>
              </MasterFormRow>

              {/* FBR Digital Invoicing: buyer province + registration type. Shown
                  only when the module is enabled — hidden for everyone else. */}
              {diEnabled && (
                <MasterFormRow>
                  <MasterFormGroup>
                    <MasterLabel>Province (FBR)</MasterLabel>
                    <MasterSelect
                      value={formData.province}
                      onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    >
                      <option value="">Select Province</option>
                      {DI_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </MasterSelect>
                  </MasterFormGroup>

                  <MasterFormGroup>
                    <MasterLabel>Registration Type (FBR)</MasterLabel>
                    <MasterSelect
                      value={formData.registrationType}
                      onChange={(e) => setFormData({ ...formData, registrationType: e.target.value })}
                    >
                      <option value="">Auto (by NTN)</option>
                      <option value="Registered">Registered</option>
                      <option value="Unregistered">Unregistered</option>
                    </MasterSelect>
                  </MasterFormGroup>
                </MasterFormRow>
              )}

              <MasterFormGroup>
                <MasterLabel>Address</MasterLabel>
                <MasterTextArea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, area"
                />
              </MasterFormGroup>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Region</MasterLabel>
                  <MasterSelect
                    value={formData.regionId}
                    onChange={(e) => handleRegionChange(e.target.value)}
                  >
                    <option value="">Select Region</option>
                    {regions.map(region => (
                      <option key={region.id} value={region.id}>{region.regionName}</option>
                    ))}
                  </MasterSelect>
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Area</MasterLabel>
                  <MasterSelect
                    value={formData.areaId}
                    onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
                    disabled={!formData.regionId}
                  >
                    <option value="">Select Area</option>
                    {getAreasForRegion(formData.regionId).map(area => (
                      <option key={area.id} value={area.id}>{area.areaName}</option>
                    ))}
                  </MasterSelect>
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Postal Code</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="74000"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Country</MasterLabel>
                  <MasterFormInput
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Pakistan"
                  />
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Credit Limit ({activeCompany?.currencyCode || 'PKR'})</MasterLabel>
                  <MasterFormInput
                    type="number"
                    step="0.01"
                    value={formData.creditLimit}
                    onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Credit Days</MasterLabel>
                  <MasterFormInput
                    type="number"
                    value={formData.creditDays}
                    onChange={(e) => setFormData({ ...formData, creditDays: parseInt(e.target.value) || 0 })}
                    placeholder="30"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Status</MasterLabel>
                  <MasterSelect
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </MasterSelect>
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Default Discount % (optional)</MasterLabel>
                  <MasterFormInput
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.defaultDiscountPercent}
                    onChange={(e) => setFormData({ ...formData, defaultDiscountPercent: e.target.value })}
                    placeholder="Leave empty for default"
                  />
                </MasterFormGroup>

                <MasterFormGroup>
                  <MasterLabel>Default Sales Tax % (optional)</MasterLabel>
                  <MasterFormInput
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.defaultTaxRate}
                    onChange={(e) => setFormData({ ...formData, defaultTaxRate: e.target.value })}
                    placeholder="Leave empty for default"
                  />
                </MasterFormGroup>
              </MasterFormRow>

              {customerClassEnabled && (
                <MasterFormRow>
                  <MasterFormGroup>
                    <MasterLabel>Customer Class (price tier)</MasterLabel>
                    <MasterSelect value={formData.customerClass} onChange={(e) => setFormData({ ...formData, customerClass: e.target.value })}>
                      <option value="">— None (uses Retail price) —</option>
                      <option value="Retail">Retail</option>
                      <option value="Wholesale">Wholesale</option>
                      <option value="Dealer">Dealer</option>
                    </MasterSelect>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                      Sale documents auto-use this price tier for this customer.
                    </div>
                  </MasterFormGroup>
                </MasterFormRow>
              )}

              <MasterFormRow>
                <MasterFormGroup>
                  <MasterLabel>Default Sales Person</MasterLabel>
                  <MasterSelect
                    value={formData.salesPersonId || ''}
                    onChange={(e) => {
                      const empId = e.target.value;
                      const emp = employees.find(em => em.id === empId);
                      const fullName = emp
                        ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || ''
                        : '';
                      setFormData({ ...formData, salesPersonId: empId, salesPersonName: fullName });
                    }}
                  >
                    <option value="">-- None --</option>
                    {employees.map(emp => {
                      const label = `${emp.employeeCode ? emp.employeeCode + ' - ' : ''}${emp.firstName || ''} ${emp.lastName || ''}`.trim()
                        || emp.employeeName
                        || '(unnamed)';
                      return <option key={emp.id} value={emp.id}>{label}</option>;
                    })}
                  </MasterSelect>
                  <small style={{ color: colors.text.tertiary, fontSize: 11, marginTop: 4 }}>
                    Auto-fills the Employee field on sale documents for this customer. Cashier can still override per document.
                  </small>
                </MasterFormGroup>
              </MasterFormRow>

              <MasterFormGroup>
                <MasterLabel>Notes</MasterLabel>
                <MasterTextArea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes or additional information"
                />
              </MasterFormGroup>
              </div>

              {customerPricingEnabled && (
                <div style={{ display: modalTab === 'pricing' ? 'block' : 'none' }}>
                  <PricingHelp>
                    Set per-customer override prices below. Whenever this customer is picked
                    on a sale, the unit price will be auto-filled from this list (instead of
                    the item's default sale price). Items not listed here keep their default
                    pricing. Toggle <strong>Restrict to listed items only</strong> to prevent
                    this customer from buying anything outside the list.
                  </PricingHelp>

                  <PricingExclusiveRow>
                    <input
                      type="checkbox"
                      id="cust-pricing-exclusive"
                      checked={formData.pricingExclusive === true}
                      onChange={(e) => setFormData({ ...formData, pricingExclusive: e.target.checked })}
                    />
                    <label htmlFor="cust-pricing-exclusive">
                      <strong>Restrict to listed items only</strong>
                      <span style={{ color: colors.text.secondary, fontSize: 12, marginLeft: 6 }}>
                        Item picker on sales forms will hide everything not listed below.
                      </span>
                    </label>
                  </PricingExclusiveRow>

                  <PricingTable>
                    <thead>
                      <tr>
                        <PricingTh style={{ width: '50%' }}>Item</PricingTh>
                        <PricingTh style={{ width: '30%', textAlign: 'right' }}>
                          Price ({activeCompany?.currencyCode || 'PKR'})
                        </PricingTh>
                        <PricingTh style={{ width: '20%', textAlign: 'center' }}>Action</PricingTh>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingRows.length === 0 && (
                        <tr>
                          <PricingTd colSpan={3} style={{ textAlign: 'center', color: colors.text.secondary, padding: '20px' }}>
                            No custom items yet. Click "Add Item" below to get started.
                          </PricingTd>
                        </tr>
                      )}
                      {pricingRows.map((row, idx) => (
                        <tr key={row.docId || `new_${idx}`}>
                          <PricingTd>
                            <ItemSelect
                              items={itemsList.filter(it =>
                                it.id === row.itemId ||
                                !pricingRows.some(r => r.itemId === it.id)
                              )}
                              selectedItemId={row.itemId}
                              onChange={(itemId) => {
                                const picked = itemsList.find(i => i.id === itemId);
                                setPricingRows(prev => {
                                  const next = [...prev];
                                  next[idx] = {
                                    ...next[idx],
                                    itemId: itemId || '',
                                    itemCode: picked?.itemCode || '',
                                    itemName: picked?.itemName || '',
                                    // Pre-seed price from item.salePrice on first pick.
                                    price: next[idx].price || Number(picked?.salePrice) || 0
                                  };
                                  return next;
                                });
                              }}
                              placeholder="Pick item"
                            />
                          </PricingTd>
                          <PricingTd>
                            <MasterFormInput
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.price}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPricingRows(prev => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], price: v === '' ? '' : Number(v) };
                                  return next;
                                });
                              }}
                              style={{ textAlign: 'right' }}
                            />
                          </PricingTd>
                          <PricingTd style={{ textAlign: 'center' }}>
                            <MasterIconButton
                              type="button"
                              onClick={() => setPricingRows(prev => prev.filter((_, i) => i !== idx))}
                              title="Remove"
                            >
                              <FiTrash2 />
                            </MasterIconButton>
                          </PricingTd>
                        </tr>
                      ))}
                    </tbody>
                  </PricingTable>

                  <MasterButton
                    type="button"
                    onClick={() => setPricingRows(prev => [
                      ...prev,
                      { docId: null, itemId: '', itemCode: '', itemName: '', price: 0, _isNew: true }
                    ])}
                    style={{ marginTop: 12 }}
                  >
                    <FiPlus /> Add Item
                  </MasterButton>
                </div>
              )}

              {subCatDiscEnabled && (
                <div style={{ display: modalTab === 'subcat' ? 'block' : 'none' }} data-scd-panel>
                  <PricingHelp>
                    Discount % for this customer on each item sub-category. When an item is added on a
                    Sale Invoice, Non-Tax Invoice, Quotation or Sale Order for this customer, its line
                    discount is taken from its sub-category. Leave blank for no special discount (the
                    customer's Default Discount % then applies, where used). Type in a category row to
                    fill all of its sub-categories.
                  </PricingHelp>
                  <MasterFormInput
                    type="text"
                    value={scdSearch}
                    onChange={(e) => setScdSearch(e.target.value)}
                    placeholder="Search category or sub-category..."
                    style={{ marginBottom: 10 }}
                    data-scd-search
                  />
                  {(() => {
                    const q = scdSearch.trim().toLowerCase();
                    const catName = (id) => (scdLists.cats.find(c => c.id === id)?.categoryName) || 'Other';
                    const groups = new Map();
                    scdLists.subs.forEach(s => {
                      const k = scdLists.cats.some(c => c.id === s.categoryId) ? s.categoryId : '__other';
                      if (!groups.has(k)) groups.set(k, []);
                      groups.get(k).push(s);
                    });
                    const list = [...groups.entries()]
                      .map(([k, subs]) => ({ k, name: k === '__other' ? 'Other' : catName(k), subs: subs.slice().sort((a, b) => (a.subCategoryName || '').localeCompare(b.subCategoryName || '')) }))
                      .sort((a, b) => (a.k === '__other') - (b.k === '__other') || a.name.localeCompare(b.name))
                      .map(g => ({ ...g, subs: (!q || g.name.toLowerCase().includes(q)) ? g.subs : g.subs.filter(s => (s.subCategoryName || '').toLowerCase().includes(q) || (s.subCategoryCode || '').toLowerCase().includes(q)) }))
                      .filter(g => g.subs.length);
                    const over = (v) => v !== '' && v != null && Number(v) > 50;
                    if (!scdLists.subs.length) {
                      return <div style={{ padding: 20, textAlign: 'center', color: colors.text.secondary, fontSize: 13 }}>No sub-categories yet. Add them in Chart of Inventory.</div>;
                    }
                    return (
                      <PricingTable>
                        <thead>
                          <tr>
                            <PricingTh style={{ width: '60%' }}>Sub-Category</PricingTh>
                            <PricingTh style={{ width: '40%' }}>Discount %</PricingTh>
                          </tr>
                        </thead>
                        <tbody>
                          {list.length === 0 && (
                            <tr><PricingTd colSpan={2} style={{ textAlign: 'center', color: colors.text.secondary }}>No match</PricingTd></tr>
                          )}
                          {list.map(g => (
                            <React.Fragment key={g.k}>
                              <ScdCatRow data-scd-category={g.k}>
                                <PricingTd>{g.name}</PricingTd>
                                <PricingTd>
                                  <ScdPctInput
                                    type="number" min="0" step="0.01" placeholder="All"
                                    title={`Set the same % for every sub-category of ${g.name}`}
                                    data-scd-fill={g.k}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setSubCatDisc(prev => {
                                        const next = { ...prev };
                                        g.subs.forEach(s => { next[s.id] = v; });
                                        return next;
                                      });
                                    }}
                                  />
                                </PricingTd>
                              </ScdCatRow>
                              {g.subs.map(s => (
                                <tr key={s.id} data-scd-sub={s.id}>
                                  <PricingTd style={{ paddingLeft: 24 }}>
                                    {s.subCategoryName}
                                    {s.subCategoryCode && <span style={{ color: colors.text.secondary, fontSize: 11, marginLeft: 6 }}>{s.subCategoryCode}</span>}
                                  </PricingTd>
                                  <PricingTd>
                                    <ScdPctInput
                                      type="number" min="0" step="0.01"
                                      value={subCatDisc[s.id] ?? ''}
                                      $warn={over(subCatDisc[s.id])}
                                      data-scd-input={s.id}
                                      onChange={(e) => { const v = e.target.value; setSubCatDisc(prev => ({ ...prev, [s.id]: v })); }}
                                    />
                                    {over(subCatDisc[s.id]) && <ScdWarn data-scd-warn title="Invoices ask to review a discount above 50% before saving">Over 50%</ScdWarn>}
                                  </PricingTd>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </PricingTable>
                    );
                  })()}
                </div>
              )}

              <MasterModalFooter>
                <MasterButton type="button" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </MasterButton>
                {canEdit && (
                  <MasterButton type="submit" primary disabled={saving}>
                    <FiSave /> {saving ? 'Saving...' : (editingCustomer ? 'Update' : 'Create')}
                  </MasterButton>
                )}
              </MasterModalFooter>
            </MasterForm>
          </MasterModalContent>
        </MasterModal>
      )}
    </PageLayout>
  );
};

export default Customers;
